'use client';

import * as React from 'react';

import { cn } from '@/lib/utils';

export type MessageAlign = 'start' | 'end';

const MessageAlignContext = React.createContext<MessageAlign>('start');

export interface MessageProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: MessageAlign;
}

const Message = React.forwardRef<HTMLDivElement, MessageProps>(
  ({ align = 'start', className, ...props }, ref) => (
    <MessageAlignContext.Provider value={align}>
      <div
        ref={ref}
        data-align={align}
        data-slot="message"
        className={cn(
          'group/message flex w-full items-end gap-2',
          align === 'end' ? 'flex-row-reverse justify-start' : 'justify-start',
          className,
        )}
        {...props}
      />
    </MessageAlignContext.Provider>
  ),
);
Message.displayName = 'Message';

const MessageGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="message-group"
      className={cn('space-y-2', className)}
      {...props}
    />
  ),
);
MessageGroup.displayName = 'MessageGroup';

const MessageAvatar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="message-avatar"
      className={cn(
        'w-7 shrink-0 self-end group-has-[[data-slot=message-footer]]/message:mb-5',
        className,
      )}
      {...props}
    />
  ),
);
MessageAvatar.displayName = 'MessageAvatar';

const MessageContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const align = React.useContext(MessageAlignContext);
    return (
      <div
        ref={ref}
        data-align={align}
        data-slot="message-content"
        className={cn(
          'flex min-w-0 max-w-[75%] flex-col',
          align === 'end' ? 'items-end' : 'items-start',
          className,
        )}
        {...props}
      />
    );
  },
);
MessageContent.displayName = 'MessageContent';

const MessageHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="message-header"
      className={cn('mb-1 self-stretch text-left text-xs text-muted-foreground', className)}
      {...props}
    />
  ),
);
MessageHeader.displayName = 'MessageHeader';

const MessageFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const align = React.useContext(MessageAlignContext);
    return (
      <div
        ref={ref}
        data-align={align}
        data-slot="message-footer"
        className={cn(
          'mt-1 flex items-center gap-2 text-[11px]',
          align === 'end' ? 'self-end justify-end' : 'self-start justify-start',
          className,
        )}
        {...props}
      />
    );
  },
);
MessageFooter.displayName = 'MessageFooter';

export {
  Message,
  MessageAvatar,
  MessageContent,
  MessageFooter,
  MessageGroup,
  MessageHeader,
};
